import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Download, Filter, RefreshCw, Loader2 } from 'lucide-react';

export type PeriodType = 'today' | 'week' | 'month' | 'custom';

interface FilterPanelProps {
  onFilterChange: (filters: {
    period: PeriodType;
    startDate?: string;
    endDate?: string;
    cameraId?: string;
    cameraIds?: string[];
  }) => void;
  cameras: Array<{ id: string; name: string }>;
  onExport?: (format: 'excel' | 'pdf') => void;
  onRefresh?: () => void;
  filterLoading?: boolean;
  refreshLoading?: boolean;
  showExportButtons?: boolean;
  /** Estado de carregamento individual por formato de exportação */
  exportingExcel?: boolean;
  exportingPdf?: boolean;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  onFilterChange,
  cameras,
  onExport,
  onRefresh,
  filterLoading = false,
  refreshLoading = false,
  showExportButtons = true,
  exportingExcel = false,
  exportingPdf = false,
}) => {
  const [period, setPeriod] = useState<PeriodType>('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCameras, setSelectedCameras] = useState<string[]>([]);
  const [cameraDropdownOpen, setCameraDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCameraDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const buildCameraParams = (ids: string[]) => {
    if (ids.length === 0) return {};
    if (ids.length === 1) return { cameraId: ids[0] };
    return { cameraIds: ids };
  };

  const handleApplyFilters = () => {
    onFilterChange({
      period,
      startDate: period === 'custom' ? startDate : undefined,
      endDate: period === 'custom' ? endDate : undefined,
      ...buildCameraParams(selectedCameras),
    });
  };

  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      onFilterChange({
        period: newPeriod,
        ...buildCameraParams(selectedCameras),
      });
    }
  };

  const handleCameraToggle = (cameraId: string) => {
    const next = selectedCameras.includes(cameraId)
      ? selectedCameras.filter(id => id !== cameraId)
      : [...selectedCameras, cameraId];
    setSelectedCameras(next);
    // Aplica filtro imediatamente ao mudar câmera (exceto no modo custom com datas incompletas)
    if (period !== 'custom' || (startDate && endDate)) {
      onFilterChange({
        period,
        startDate: period === 'custom' ? startDate : undefined,
        endDate: period === 'custom' ? endDate : undefined,
        ...buildCameraParams(next),
      });
    }
  };

  const handleClearCameras = () => {
    setSelectedCameras([]);
    if (period !== 'custom' || (startDate && endDate)) {
      onFilterChange({
        period,
        startDate: period === 'custom' ? startDate : undefined,
        endDate: period === 'custom' ? endDate : undefined,
      });
    }
  };

  const cameraLabel =
    selectedCameras.length === 0
      ? 'Todas as Câmeras'
      : selectedCameras.length === 1
        ? cameras.find(c => c.id === selectedCameras[0])?.name ?? selectedCameras[0]
        : `${selectedCameras.length} câmeras selecionadas`;

  const daysDiff =
    period === 'custom' && startDate && endDate
      ? Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1
      : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6">
      <div className="flex flex-col gap-3">
        {/* Linha 1: Período rápido + Exportação/Atualizar (à direita no desktop;
            empilhado em largura total no mobile) */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Filtro Rápido de Período */}
          <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
            {[
              { value: 'today', label: 'Hoje' },
              { value: 'week', label: '7 Dias' },
              { value: 'month', label: '30 Dias' },
              { value: 'custom', label: 'Personalizado' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => handlePeriodChange(opt.value as PeriodType)}
                className={`px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base whitespace-nowrap ${
                  period === opt.value
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Botões de Exportação + Atualizar */}
          {showExportButtons && onExport && (
            <div className="flex w-full sm:w-auto flex-wrap gap-2">
              <button
                onClick={() => onExport('excel')}
                disabled={filterLoading || exportingExcel || exportingPdf}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
              >
                {exportingExcel ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                {exportingExcel ? 'Gerando Excel...' : 'Excel'}
              </button>
              <button
                onClick={() => onExport('pdf')}
                disabled={filterLoading || exportingExcel || exportingPdf}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
              >
                {exportingPdf ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                {exportingPdf ? 'Gerando PDF...' : 'PDF'}
              </button>
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={refreshLoading}
                  className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm"
                >
                  <RefreshCw size={16} className={refreshLoading ? 'animate-spin' : ''} />
                  Atualizar
                </button>
              )}
            </div>
          )}
        </div>

        {/* Linha 2: Filtros sempre visíveis.
            Câmera aparece sempre; no modo Personalizado entram lado a lado
            Data Início, Data Fim e o botão Aplicar Filtro (ao lado do Data Fim). */}
        <div className="mt-2 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-end gap-3 sm:gap-4">
          {/* Seletor de Câmera — multi-select (sempre visível) */}
          <div ref={dropdownRef} className="relative w-full sm:w-64">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Câmeras
            </label>
            <button
              type="button"
              onClick={() => setCameraDropdownOpen(prev => !prev)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-left flex items-center justify-between"
            >
              <span className="truncate">{cameraLabel}</span>
              <svg
                className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${cameraDropdownOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {cameraDropdownOpen && (
              <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {/* Todas as câmeras */}
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm border-b border-gray-100 dark:border-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedCameras.length === 0}
                    onChange={handleClearCameras}
                    className="accent-blue-500"
                  />
                  <span className="text-gray-700 dark:text-gray-200 font-medium">Todas as Câmeras</span>
                </label>
                {cameras.map((camera) => (
                  <label key={camera.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selectedCameras.includes(camera.id)}
                      onChange={() => handleCameraToggle(camera.id)}
                      className="accent-blue-500"
                    />
                    <span className="text-gray-700 dark:text-gray-200 truncate">{camera.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Datas + Aplicar (apenas período custom) — lado a lado */}
          {period === 'custom' && (
            <>
              <div className="w-full sm:w-44">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Data Início
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="w-full sm:w-44">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Data Fim
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Botão Aplicar Filtro — ao lado do Data Fim */}
              <div className="w-full sm:w-auto">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">&nbsp;</span>
                <button
                  onClick={handleApplyFilters}
                  disabled={!startDate || !endDate || filterLoading}
                  className={`w-full sm:w-auto px-5 py-2 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 ${
                    startDate && endDate && !filterLoading
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  {filterLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Carregando...</span>
                    </>
                  ) : (
                    <>
                      <Filter size={16} />
                      Aplicar Filtro
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Aviso de período extenso */}
        {daysDiff > 45 && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${
            daysDiff > 90
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
              : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300'
          }`}>
            <span className="shrink-0 mt-0.5">{daysDiff > 90 ? '🔴' : '⚠️'}</span>
            <span>
              <strong>Período extenso: {daysDiff} dias.</strong>{' '}
              {daysDiff > 90
                ? 'Consultas acima de 90 dias podem levar vários minutos ou atingir o tempo limite. Considere um intervalo menor.'
                : 'O carregamento pode demorar mais que o habitual. Aguarde enquanto os dados são processados.'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
